import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SrIcon } from "@/components/ui/SrIcon";
import { useCustomers } from "@/hooks/use-customers";
import {
  useRecurringAgreementMutations,
  type RecurringAgreementWithCustomer,
} from "@/hooks/use-recurring-agreements";
import { computeNextRunDate } from "@/lib/recurring-agreements-calc";
import type { RecurringAgreementLine } from "@/lib/db-types";

interface AgreementLineDraft extends RecurringAgreementLine {
  key: string;
}

function emptyLine(): AgreementLineDraft {
  return { key: crypto.randomUUID(), description: "", quantity: 1, unit_price: 0 };
}

interface AgreementFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Satt ved redigering; utelatt = ny avtale. */
  agreement?: RecurringAgreementWithCustomer;
  /** Forhåndsvalgt kunde (f.eks. åpnet fra kundekortet — VIB-1290). */
  defaultCustomerId?: string;
}

/**
 * Opprett/rediger recurring- eller rental-avtale. Storage-avtaler (VIB-1308)
 * opprettes IKKE via denne dialogen — de genereres automatisk fra
 * prosjekt-lukkeflyten og redigeres via GB-feltene der.
 */
export function AgreementFormDialog({
  open,
  onOpenChange,
  agreement,
  defaultCustomerId,
}: AgreementFormDialogProps) {
  const isEdit = !!agreement;
  const { data: customers = [] } = useCustomers();
  const { createAgreement, updateAgreement } = useRecurringAgreementMutations();

  const [customerId, setCustomerId] = useState(agreement?.customer_id ?? defaultCustomerId ?? "");
  const [type, setType] = useState<"recurring" | "rental">(
    agreement?.type === "rental" ? "rental" : "recurring",
  );
  const [description, setDescription] = useState(agreement?.description ?? "");
  const [rentalObject, setRentalObject] = useState(agreement?.rental_object ?? "");
  const [billingInterval, setBillingInterval] = useState<"monthly" | "quarterly" | "yearly">(
    agreement?.billing_interval ?? "monthly",
  );
  const [startDate, setStartDate] = useState(agreement?.start_date ?? "");
  const [endDate, setEndDate] = useState(agreement?.end_date ?? "");
  const [autoSend, setAutoSend] = useState(agreement?.auto_send ?? false);
  const [lines, setLines] = useState<AgreementLineDraft[]>(
    agreement?.lines?.length
      ? agreement.lines.map((l) => ({ ...l, key: crypto.randomUUID() }))
      : [emptyLine()],
  );

  // Reset skjemaet når dialogen åpnes på nytt for en annen avtale/ny avtale.
  useEffect(() => {
    if (!open) return;
    setCustomerId(agreement?.customer_id ?? defaultCustomerId ?? "");
    setType(agreement?.type === "rental" ? "rental" : "recurring");
    setDescription(agreement?.description ?? "");
    setRentalObject(agreement?.rental_object ?? "");
    setBillingInterval(agreement?.billing_interval ?? "monthly");
    setStartDate(agreement?.start_date ?? "");
    setEndDate(agreement?.end_date ?? "");
    setAutoSend(agreement?.auto_send ?? false);
    setLines(
      agreement?.lines?.length
        ? agreement.lines.map((l) => ({ ...l, key: crypto.randomUUID() }))
        : [emptyLine()],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agreement?.id]);

  const updateLine = (key: string, patch: Partial<AgreementLineDraft>) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };
  const removeLine = (key: string) => {
    setLines((prev) => (prev.length > 1 ? prev.filter((l) => l.key !== key) : prev));
  };

  const isSaving = createAgreement.isPending || updateAgreement.isPending;
  const canSave =
    !!customerId &&
    !!startDate &&
    lines.some((l) => l.description.trim() && l.unit_price > 0);

  const handleSave = async () => {
    const cleanLines: RecurringAgreementLine[] = lines
      .filter((l) => l.description.trim())
      .map(({ key: _key, ...rest }) => rest);

    if (isEdit && agreement) {
      await updateAgreement.mutateAsync({
        id: agreement.id,
        description: description || null,
        rental_object: type === "rental" ? rentalObject || null : null,
        lines: cleanLines,
        billing_interval: billingInterval,
        start_date: startDate,
        end_date: endDate || null,
        auto_send: autoSend,
      });
    } else {
      await createAgreement.mutateAsync({
        customer_id: customerId,
        type,
        description: description || null,
        rental_object: type === "rental" ? rentalObject || null : null,
        lines: cleanLines,
        billing_interval: billingInterval,
        start_date: startDate,
        end_date: endDate || null,
        // Første fakturering skjer ved neste kjøring FRA startdato — samme
        // intervall-bump som motoren bruker senere (VIB-1292).
        next_run_date: computeNextRunDate(startDate, billingInterval),
        auto_send: autoSend,
      });
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Rediger avtale" : "Ny avtale"}</DialogTitle>
        </DialogHeader>

        {/* <form> så Enter i et tekstfelt lagrer når skjemaet er gyldig (VIB-2544). */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSave || isSaving) return;
            void handleSave();
          }}
        >
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="agreement-customer">Kunde</Label>
            <Select value={customerId} onValueChange={setCustomerId} disabled={isEdit}>
              <SelectTrigger id="agreement-customer" aria-label="Velg kunde">
                <SelectValue placeholder="Velg kunde" />
              </SelectTrigger>
              <SelectContent>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Avtaletype</Label>
            <Select
              value={type}
              onValueChange={(v) => setType(v as "recurring" | "rental")}
              disabled={isEdit}
            >
              <SelectTrigger aria-label="Velg avtaletype">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recurring">Løpende avtale</SelectItem>
                <SelectItem value="rental">Utleieavtale</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {type === "rental" && (
            <div className="space-y-1.5">
              <Label htmlFor="agreement-rental-object">Utleieobjekt</Label>
              <Input
                id="agreement-rental-object"
                value={rentalObject}
                onChange={(e) => setRentalObject(e.target.value)}
                placeholder="F.eks. kamerarigg, lagerplass ..."
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="agreement-description">Beskrivelse</Label>
            <Input
              id="agreement-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Vises på fakturaen"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="agreement-start-date">Startdato</Label>
              <Input
                id="agreement-start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="agreement-end-date">Sluttdato (valgfri)</Label>
              <Input
                id="agreement-end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Faktureringsintervall</Label>
            <Select
              value={billingInterval}
              onValueChange={(v) => setBillingInterval(v as "monthly" | "quarterly" | "yearly")}
            >
              <SelectTrigger aria-label="Velg faktureringsintervall">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Månedlig</SelectItem>
                <SelectItem value="quarterly">Kvartalsvis</SelectItem>
                <SelectItem value="yearly">Årlig</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Linjer</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setLines((prev) => [...prev, emptyLine()])}
              >
                <SrIcon name="plus" className="w-3 h-3 mr-1" /> Legg til linje
              </Button>
            </div>
            {lines.map((line) => (
              <div key={line.key} className="flex gap-2 items-start">
                <Input
                  aria-label="Linjebeskrivelse"
                  value={line.description}
                  onChange={(e) => updateLine(line.key, { description: e.target.value })}
                  placeholder="Beskrivelse"
                  className="flex-1"
                />
                <Input
                  aria-label="Antall"
                  type="number"
                  min={0}
                  value={line.quantity}
                  onChange={(e) => updateLine(line.key, { quantity: Number(e.target.value) })}
                  className="w-16"
                />
                <Input
                  aria-label="Pris"
                  type="number"
                  min={0}
                  value={line.unit_price}
                  onChange={(e) => updateLine(line.key, { unit_price: Number(e.target.value) })}
                  className="w-24"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  onClick={() => removeLine(line.key)}
                  disabled={lines.length <= 1}
                  aria-label="Fjern linje"
                >
                  <SrIcon name="x" className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="agreement-auto-send"
              checked={autoSend}
              onCheckedChange={(v) => setAutoSend(v === true)}
            />
            <Label htmlFor="agreement-auto-send" className="text-sm font-normal">
              Send faktura automatisk (krever at global driftsbryter også er på)
            </Label>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
          <Button type="submit" disabled={!canSave || isSaving}>
            {isSaving ? "Lagrer …" : isEdit ? "Lagre endringer" : "Opprett avtale"}
          </Button>
        </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
